import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ordersApi, driversApi, shopAPI } from '../utils/api';

// Types
export interface Order {
    order_number: string;
    shop_name: string;
    customer_name?: string;
    status: string;
    location: string;
    time_slot: string;
    /** From the customer's WhatsApp location reply; null when they never sent one. */
    latitude?: number | null;
    longitude?: number | null;
    assigned_driver?: string;
    payment_status?: string;
    delivery_cost?: number;
    delivery_cost_paid?: boolean;
    picked_up?: boolean;
    is_return?: boolean;
    original_order_id?: string;
    return_reason?: string;
}

export interface Driver {
    id?: string;
    name: string;
    phone: string;
}

export interface Store {
    id?: string;
    name: string;
    latitude: number;
    longitude: number;
    phone: string;
    product_type: string;
    cr_number: string;
}

export interface ShopApplication {
    id: string;
    user_id: string;
    business_name: string;
    business_type: string;
    shop_link?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    orders_per_day?: string;
    phone?: string;
    status: 'pending' | 'approved' | 'declined';
    decline_reason?: string;
    created_at: string;
}

interface DataContextType {
    // Orders
    orders: Order[];
    ordersLoading: boolean;
    ordersError: string | null;
    refreshOrders: () => Promise<void>;
    markOrderPickedUp: (orderId: string) => void;

    // Drivers
    drivers: Driver[];
    driversLoading: boolean;
    driversError: string | null;
    refreshDrivers: () => Promise<void>;

    // Stores
    stores: Store[];
    storesLoading: boolean;
    storesError: string | null;
    refreshStores: () => Promise<void>;

    // Applications
    applications: ShopApplication[];
    applicationsLoading: boolean;
    applicationsError: string | null;
    refreshApplications: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Orders State
    const [orders, setOrders] = useState<Order[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const [ordersLoaded, setOrdersLoaded] = useState(false);

    // Drivers State
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [driversLoading, setDriversLoading] = useState(false);
    const [driversError, setDriversError] = useState<string | null>(null);
    const [driversLoaded, setDriversLoaded] = useState(false);

    // Stores State
    const [stores, setStores] = useState<Store[]>([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [storesError, setStoresError] = useState<string | null>(null);
    const [storesLoaded, setStoresLoaded] = useState(false);

    // Applications State
    const [applications, setApplications] = useState<ShopApplication[]>([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [applicationsError, setApplicationsError] = useState<string | null>(null);
    const [applicationsLoaded, setApplicationsLoaded] = useState(false);


    // --- Orders Logic ---
    const refreshOrders = async () => {
        setOrdersLoading(true);
        setOrdersError(null);
        try {
            const data = await ordersApi.getAll();
            setOrders(data);
            setOrdersLoaded(true);
        } catch (err: any) {
            console.error('Error fetching orders:', err);
            setOrdersError(err.message || 'Failed to fetch orders');
        } finally {
            setOrdersLoading(false);
        }
    };

    const markOrderPickedUp = (orderId: string) => {
        setOrders(prev => prev.map(o =>
            o.order_number === orderId ? { ...o, picked_up: true } : o
        ));
    };

    // --- Drivers Logic ---
    const refreshDrivers = async () => {
        setDriversLoading(true);
        setDriversError(null);
        try {
            const data = await driversApi.getAll();
            setDrivers(data || []);
            setDriversLoaded(true);
        } catch (err: any) {
            console.error('Error fetching drivers:', err);
            setDriversError(err.message || 'Failed to fetch drivers');
        } finally {
            setDriversLoading(false);
        }
    };

    // --- Stores Logic ---
    const refreshStores = async () => {
        setStoresLoading(true);
        setStoresError(null);
        try {
            const data = await shopAPI.getAll();
            setStores(data);
            setStoresLoaded(true);
        } catch (err: any) {
            console.error('Error fetching stores:', err);
            setStoresError(err.message || 'Failed to fetch stores');
        } finally {
            setStoresLoading(false);
        }
    };

    // --- Applications Logic ---
    const refreshApplications = async () => {
        setApplicationsLoading(true);
        setApplicationsError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/shop-applications/all`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setApplications(data.data);
                setApplicationsLoaded(true);
            } else {
                setApplicationsError('Failed to fetch applications');
            }
        } catch (err: any) {
            console.error('Error fetching applications:', err);
            setApplicationsError(err.message || 'Failed to fetch applications');
        } finally {
            setApplicationsLoading(false);
        }
    };

    // Initial Load Effects - only fetch if not loaded
    useEffect(() => {
        if (!ordersLoaded && !ordersLoading) refreshOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!driversLoaded && !driversLoading) refreshDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!storesLoaded && !storesLoading) refreshStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!applicationsLoaded && !applicationsLoading) refreshApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <DataContext.Provider value={{
            orders, ordersLoading, ordersError, refreshOrders, markOrderPickedUp,
            drivers, driversLoading, driversError, refreshDrivers,
            stores, storesLoading, storesError, refreshStores,
            applications, applicationsLoading, applicationsError, refreshApplications
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
